import { ComponentAPI } from '@philips-emr/tws-core';
import { EventDelegator, EventMapper } from '@philips-emr/tws-core/lib/component/event';
import EventData from '@philips-emr/tws-core/lib/component/event/event-data';
import { EventCallback, EventMap } from '@philips-emr/tws-core/lib/component/event/event-delegator';
import { Address } from '@philips-emr/tws-core/lib/component/event/event-mapper';
import { eventListener } from '@philips-emr/tws-core/lib/component/prop-types';

import ComponentContext from './component-node';

const lifecycleEvents = ['onMount', 'onUnmount'];

/**
 * Engine's EventDelegator, responsible to gather events from React API and DI Container.
 * The fire order is:
 * - Callback comming from React parent node;
 * - Callbacks binded by API;
 * - Callbacks binded by DI Container.
 */
class EngineEventDelegator extends EventDelegator<ComponentAPI, EventData<ComponentAPI>> {

  context: ComponentContext;
  props: {
    [key: string]: any;
  } = {};

  constructor(context: ComponentContext) {
    super();
    this.context = context;

    const propTypes = context.getRenderable().propTypes || {};
    const eventProps = Object.keys(propTypes).filter(p => propTypes[p] === eventListener);

    eventProps.concat(lifecycleEvents).forEach(event => {
      const callback = this.generate(event);
      this.events[event] = callback;
    });
  }

  private generate(event: string): EventCallback {
    return (...args: any[]) => {
      const { context } = this;
      const api = context.getAPI();
      const owner = context.getOwner();
      const container = owner.getContainer();

      try {
        // first check parent callback
        const callback = this.props[event];
        callback && callback(...args);
      } catch(e) {
        const c = console;
        c.log('[ENGINE]: Problem to invoke callback', e);
      }

      const eventListener = api && api[event];
      if (eventListener) {
        try {
          const eventData = eventListener.prepare(...args);

          // second check handler's callbacks
          const eventsByBus = this.eventBus.get(event);
          eventsByBus && eventsByBus.forEach((callback: Function) => callback(eventData));

          // third check controller's callbacks
          if (container) {
            const alias = context.getSSOT().state.alias;

            if (alias) {
              const map = EventMapper.getComponent(owner.getName(), alias);

              if (map) {
                const addresses = map.get(event) || [];
                addresses.forEach(({ className, methodName }: Address) => {
                  const instance = container.provide(className, true);

                  const valid = instance && instance[methodName];
                  valid && instance[methodName](eventData);
                }); 
              }
            }
          }
        } catch(e) {
          const c = console;
          c.log('[ENGINE]: Problem to invoke callback', e);
        }
      }
    };
  }

  fire(type: string, event?: unknown): void {
    const callback = this.events[type];
    if (callback) {
      callback(event);
    }
  }

  /**
   * Retrieves all event's callbacks.
   * It needs to keep the current props comming form parent, to capture possible event callbacks.
   * 
   * @param props the props comming from parent.
   * @returns the events callbacks.
   */
  collect(props: object): EventMap {
    this.props = props;
    return this.events;
  }

}

export default EngineEventDelegator;
